import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { AdviseSupplier } from '../types/advise';
import {
  fetchAdviseSuppliers as apiFetchAdviseSuppliers,
  createAdviseSupplier as apiCreateAdviseSupplier,
  updateAdviseSupplier as apiUpdateAdviseSupplier,
  deleteAdviseSupplier as apiDeleteAdviseSupplier
} from '../Api/Advise/Suppliers';
import { addError } from './Error';

export interface UseAdviseSuppliersReturn {
  suppliers: AdviseSupplier[];
  fetchSuppliers: (forceRefresh?: boolean) => Promise<void>;
  createSupplier: (supplier: Partial<AdviseSupplier>) => Promise<AdviseSupplier | null>;
  updateSupplier: (id: string, supplier: Partial<AdviseSupplier>) => Promise<AdviseSupplier | null>;
  deleteSupplier: (id: string) => Promise<boolean>;
  clear: () => void;
}

const defaultData: AdviseSupplier[] = [];
const subject = new BehaviorSubject<AdviseSupplier[]>(defaultData);
const loadingSubject = new BehaviorSubject<boolean>(false);
const emitErrorState = (message?: string): void => addError('advise-supplier', message);

export const getAdviseSupplierSubject = (): BehaviorSubject<AdviseSupplier[]> => subject;
export const getAdviseSuppliers = (): AdviseSupplier[] => subject.value;
export const getAdviseSupplierLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;

export const useAdviseSuppliers = (): UseAdviseSuppliersReturn => {
  const [suppliers, setSuppliers] = useState(subject.getValue());

  useEffect(() => {
    const sub = subject.subscribe(setSuppliers);
    return () => sub.unsubscribe();
  }, []);

  const fetchSuppliers = async (forceRefresh = false): Promise<void> => {
    if (loadingSubject.getValue()) return;
    if (subject.getValue().length > 0 && !forceRefresh) return;
    loadingSubject.next(true);

    const { data, error } = await apiFetchAdviseSuppliers();
    if (data) {
      emitErrorState();
      subject.next(data);
      setSuppliers(data);
    }
    if (error) {
      subject.next(defaultData);
      emitErrorState(error.message);
    }
    loadingSubject.next(false);
  };

  const createSupplier = async (supplier: Partial<AdviseSupplier>): Promise<AdviseSupplier | null> => {
    const { data, error } = await apiCreateAdviseSupplier(supplier);
    if (data) {
      emitErrorState();
      const updated = [...subject.getValue(), data];
      subject.next(updated);
      setSuppliers(updated);
      return data;
    }
    if (error) emitErrorState(error.message);
    return null;
  };

  const updateSupplier = async (id: string, supplier: Partial<AdviseSupplier>): Promise<AdviseSupplier | null> => {
    const { data, error } = await apiUpdateAdviseSupplier(id, supplier);
    if (data) {
      emitErrorState();
      const updated = subject.getValue().map((v) => (v.id === id ? data : v));
      subject.next(updated);
      setSuppliers(updated);
      return data;
    }
    if (error) emitErrorState(error.message);
    return null;
  };

  const deleteSupplier = async (id: string): Promise<boolean> => {
    const { error } = await apiDeleteAdviseSupplier(id);
    if (!error) {
      emitErrorState();
      const updated = subject.getValue().filter((v) => v.id !== id);
      subject.next(updated);
      setSuppliers(updated);
      return true;
    }
    emitErrorState(error.message);
    return false;
  };

  const clear = () => {
    subject.next(defaultData);
    setSuppliers(defaultData);
  };

  return { suppliers, fetchSuppliers, createSupplier, updateSupplier, deleteSupplier, clear };
};
